import { GoogleGenAI, Type } from '@google/genai';
import { Case, LandTransporter, DispatchPlan, SuggestedTransporterInfo } from '../types';
import { dispatchWithTimeout, apiWithTimeout } from '../utils/apiTimeout';
import logger from '../utils/logger';
import { getGeminiApiKey } from '../utils/environment';

// Gemini API キーを統一的な方法で取得
// getGeminiApiKey()により以下のフォールバックが適用される:
// 1. VITE_GEMINI_API_KEY (推奨)
// 2. GEMINI_API_KEY (レガシー)
// 3. API_KEY (レガシー)
const API_KEY = getGeminiApiKey();

if (!API_KEY) {
  // In a real app, you might have a more robust way to handle this,
  // but for this example, we'll throw an error if the key is missing.
  logger.error('Gemini API key environment variable not set.');
}

const ai = new GoogleGenAI({ apiKey: API_KEY! });

const dispatchPlanSchema = {
  type: Type.ARRAY,
  items: {
    type: Type.OBJECT,
    properties: {
      landTransporterId: {
        type: Type.STRING,
        description: '陸送員のID。',
      },
      landTransporterName: {
        type: Type.STRING,
        description: '参照しやすいように陸送員の名前。',
      },
      caseIds: {
        type: Type.ARRAY,
        description: 'この陸送員に割り当てられた案件IDの配列。',
        items: {
          type: Type.STRING,
        },
      },
      reason: {
        type: Type.STRING,
        description: 'この陸送員を選択した理由の説明文（100文字程度）。',
      },
      factors: {
        type: Type.ARRAY,
        description: '選択要因のリスト（時間適合性、車両適合性、効率性、地理的要因等）。',
        items: {
          type: Type.STRING,
        },
      },
      assignedTimeSlots: {
        type: Type.OBJECT,
        description: '割り当てられた案件の時間スロット情報。',
        properties: {
          startTime: {
            type: Type.STRING,
            description: '最初の案件開始時間（HH:mm形式）。',
          },
          endTime: {
            type: Type.STRING,
            description: '最後の案件終了時間（HH:mm形式）。',
          },
          totalDuration: {
            type: Type.NUMBER,
            description: '総稼働時間（分単位）。',
          },
        },
        required: ['startTime', 'endTime', 'totalDuration'],
      },
      workingHours: {
        type: Type.OBJECT,
        description: '陸送員の勤務時間情報。',
        properties: {
          start: {
            type: Type.STRING,
            description: '勤務開始時間（HH:mm形式）。',
          },
          end: {
            type: Type.STRING,
            description: '勤務終了時間（HH:mm形式）。',
          },
        },
        required: ['start', 'end'],
      },
      // FL-270: 最寄駅情報（Google Maps API統合）
      nearestStation: {
        type: Type.STRING,
        description: '陸送員の最寄駅名（設定されている場合）。',
      },
      nearestStationLat: {
        type: Type.NUMBER,
        description: '陸送員の最寄駅の緯度（設定されている場合）。',
      },
      nearestStationLng: {
        type: Type.NUMBER,
        description: '陸送員の最寄駅の経度（設定されている場合）。',
      },
    },
    required: [
      'landTransporterId',
      'landTransporterName',
      'caseIds',
      'reason',
      'factors',
      'assignedTimeSlots',
      'workingHours',
    ],
  },
};

export const generateDispatchPlan = async (
  cases: Case[],
  landTransporters: LandTransporter[]
): Promise<DispatchPlan[]> => {
  try {
    return await dispatchWithTimeout(
      async () => {
        const prompt = `
                あなたは車両輸送会社の熟練した物流コーディネーターAIです。あなたのタスクは、最適な配車計画を作成することです。
                以下の未割り当ての案件を、対応可能な陸送員に割り当ててください。

                **制約と目標:**
                1.  各陸送員は複数の案件を処理できます。
                2.  案件の \`vehicleType\`（すでにAIによって判別済みの車種タイプ）が、陸送員の \`vehicleTypes\`（対応可能な車種タイプの配列）に含まれていることを確認してください。
                3.  陸送員間で作業負荷を均等に分散するようにしてください。
                4.  住所に基づき、地理的に近い案件を同じ陸送員に優先的に割り当ててください。
                5.  すべての案件が、必ず一人の陸送員に割り当てられるようにしてください。
                6.  適切な案件がない、または案件数が少ない場合、陸送員に案件が割り当てられないこともあります。
                7.  計画を指定されたJSON形式で返してください。
                8.  各陸送員の曜日ごとの勤務時間帯（workSchedule）を厳守してください。勤務時間外の案件割り当ては避けてください。本日が何曜日かを考慮する必要はありません。一般的な可用性としてスケジュールを使用してください。
                9.  陸送員の以下の特性を厳密に考慮してください:
                    - \`cannotDriveTruck\` が true の陸送員には、\`vehicleType\` が 'トラック' の案件を割り当ててはいけません。
                10. 各陸送員を選択した理由を必ず含めてください:
                    - \`reason\`: その陸送員を選択した理由を100文字程度で説明してください。
                    - \`factors\`: 選択要因を具体的にリストアップしてください（例：「時間適合性」「車両適合性」「地理的効率性」「負荷分散」「最寄駅利便性」など）。
                11. 時間情報を必ず含めてください:
                    - \`assignedTimeSlots\`: 割り当てた案件の最初の引取開始時間から最後の搬入完了時間までの情報
                    - \`workingHours\`: その陸送員の当日の勤務時間（workScheduleから取得）
                    - 案件の時間は \`pickupTimeStart\`, \`pickupTimeEnd\`, \`deliveryTime\` を参考にしてください
                12. **FL-270: 最寄駅情報の活用** (新規)
                    - 各陸送員の \`nearestStation\` フィールドに駅名が設定されている場合、その駅を起点とする移動効率を考慮してください。
                    - 陸送員の \`nearestStationLat\`, \`nearestStationLng\` を参考に、案件の引取・搬入先までの距離を概算してください。
                    - 複数の陸送員が同等の条件の場合、最寄駅が近い者（最寄駅情報が充実している者）を優先してください。
                    - 最寄駅情報を活用した割り当ての場合、理由（reason）に「最寄駅からの利便性」を含めてください。

                **対応可能な陸送員:**
                ${JSON.stringify(landTransporters, null, 2)}

                **未割り当ての案件:**
                ${JSON.stringify(cases, null, 2)}

                このデータに基づいて、配車計画を生成してください。
            `;

        const response = await ai.models.generateContent({
          model: 'gemini-2.5-flash',
          contents: prompt,
          config: {
            responseMimeType: 'application/json',
            responseSchema: dispatchPlanSchema,
            // Disable thinking for faster, low-latency response suitable for a dispatch tool
            thinkingConfig: { thinkingBudget: 0 },
          },
        });

        const jsonText = response.text?.trim() || '';
        const plan: DispatchPlan[] = JSON.parse(jsonText);
        return plan;
      },
      {
        casesCount: cases.length,
        transportersCount: landTransporters.length,
      }
    );
  } catch (error) {
    logger.error('配車計画の生成中にエラーが発生しました:', error);
    throw new Error('AI配車計画の生成に失敗しました。詳細はコンソールを確認してください。');
  }
};

const suggestedTransportersSchema = {
  type: Type.ARRAY,
  description: '推奨される陸送員の配列。それぞれにIDと推薦理由が含まれる。最大3名まで。',
  items: {
    type: Type.OBJECT,
    properties: {
      transporterId: {
        type: Type.STRING,
        description: '推奨される陸送員のID。',
      },
      reason: {
        type: Type.STRING,
        description: 'この陸送員を推奨する簡潔な理由。',
      },
    },
    required: ['transporterId', 'reason'],
  },
};

export const getSuggestedLandTransporters = async (
  targetCase: Case,
  landTransporters: LandTransporter[]
): Promise<SuggestedTransporterInfo[]> => {
  try {
    return await apiWithTimeout(
      async () => {
        const prompt = `
                あなたは熟練した物流コーディネーターAIです。
                以下の単一案件に最も適した陸送員を、提示されたリストの中から最大3名まで推薦してください。

                **推薦の評価基準:**
                1.  **地理的近接性:** 案件の引取場所(\`pickupAddress\`)と陸送員の住所(\`address\`)が近いことを最優先します。
                2.  **車両の互換性:** 案件の\`vehicleType\`（判別済みの車種タイプ）が、陸送員の対応車種タイプの配列(\`vehicleTypes\`)に含まれている必要があります。
                3.  **制約の遵守:** 陸送員の\`cannotDriveTruck\`がtrueの場合、案件の\`vehicleType\`が'トラック'の場合は推薦しないでください。
                4.  **FL-270: 最寄駅情報の活用** (新規)
                    - 各陸送員の \`nearestStation\` フィールドに駅名が設定されている場合、その駅を起点とする移動効率を考慮してください。
                    - 陸送員の \`nearestStationLat\`, \`nearestStationLng\` を参考に、案件の引取・搬入先までの距離を概算してください。
                    - 複数の陸送員が同等の条件の場合、最寄駅が近い者（最寄駅情報が充実している者）を優先してください。
                    - 最寄駅情報を活用した推薦の場合、理由（reason）に「最寄駅からの利便性」を含めてください。

                **対象案件:**
                ${JSON.stringify(targetCase, null, 2)}

                **陸送員候補リスト:**
                ${JSON.stringify(landTransporters, null, 2)}

                上記の情報に基づき、最も適した陸送員を推薦してください。
                結果は、陸送員のIDと推薦理由を含むオブジェクトの配列として、指定されたJSON形式で返してください。
            `;

        const response = await ai.models.generateContent({
          model: 'gemini-2.5-flash',
          contents: prompt,
          config: {
            responseMimeType: 'application/json',
            responseSchema: suggestedTransportersSchema,
            thinkingConfig: { thinkingBudget: 0 },
          },
        });

        const jsonText = response.text?.trim() || '';
        const suggestions: SuggestedTransporterInfo[] = JSON.parse(jsonText);
        return suggestions;
      },
      'suggest-land-transporters',
      undefined,
      {
        metadata: {
          caseId: targetCase.id,
          transportersCount: landTransporters.length,
        },
      }
    );
  } catch (error) {
    logger.error('陸送員の推薦中にエラーが発生しました:', error);
    // In case of an error, return an empty array to not break the UI
    return [];
  }
};

const vehicleTypeSchema = {
  type: Type.OBJECT,
  properties: {
    type: {
      type: Type.STRING,
      description: '判別された車両タイプ (例: セダン, トラック, SUV, バン, バイク, 軽自動車など)。',
    },
  },
  required: ['type'],
};

export const getVehicleTypeFromModelName = async (
  modelName: string,
  availableTypes: string[]
): Promise<string> => {
  try {
    return await apiWithTimeout(
      async () => {
        const prompt = `
                あなたは自動車のエキスパートAIです。
                以下の車両モデル名から、その車両タイプを判別してください。
                回答は、以下のリストの中から最も適切なものを1つだけ選んでください。
                もしリスト内に適切なものがなければ、最も近いと思われる一般的な車両タイプ（例: セダン, トラック, SUV）を回答してください。

                利用可能な車両タイプリスト: [${availableTypes.join(', ')}]

                **車両モデル名:**
                ${modelName}

                結果を指定されたJSON形式で返してください。
            `;

        const response = await ai.models.generateContent({
          model: 'gemini-2.5-flash',
          contents: prompt,
          config: {
            responseMimeType: 'application/json',
            responseSchema: vehicleTypeSchema,
            thinkingConfig: { thinkingBudget: 0 },
          },
        });

        const jsonText = response.text?.trim() || '';
        const result = JSON.parse(jsonText);
        return result.type || '不明';
      },
      'vehicle-type-classification',
      undefined,
      {
        metadata: {
          modelName,
          availableTypesCount: availableTypes.length,
        },
      }
    );
  } catch (error) {
    logger.error(`モデル名からの車種タイプ判別中にエラーが発生しました (${modelName}):`, error);
    return '不明';
  }
};
